import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, KeyRound, MailOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (d) => {
  if (!d) return "The door is momentarily unreachable. Try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(d?.msg || d);
};

const inputClass =
  "border border-[#c9a227]/25 bg-transparent px-5 py-3.5 text-center text-sm font-light tracking-wide text-[#fdfbf7] placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none transition-colors duration-300";

const submitClass =
  "group relative overflow-hidden border border-[#c9a227]/50 py-3.5 text-[12px] uppercase tracking-[0.32em] text-[#e8c95a] transition-colors duration-500 hover:text-[#050a06] disabled:opacity-50";

const MemberLogin = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const close = () => {
    setOpen(false);
    setError("");
    setTimeout(() => setView("login"), 400);
  };

  const switchView = (v) => {
    setError("");
    setView(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email: email.trim(), password }, { withCredentials: true });
      navigate("/society");
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitRecovery = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/recover-key`, { email: email.trim() }, { withCredentials: true });
      switchView("sent");
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="member-login-trigger"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.2, duration: 1 }}
        aria-label="Members sign in"
        className="fixed bottom-7 left-[5.25rem] z-40 flex h-12 w-12 items-center justify-center rounded-full border border-[#c9a227]/35 bg-black/50 text-[#c9a227]/80 backdrop-blur-md transition-all duration-500 hover:border-[#c9a227]/70 hover:text-[#e8c95a] hover:shadow-[0_0_30px_rgba(201,162,39,0.35)]"
      >
        <KeyRound size={18} strokeWidth={1.3} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center px-5" data-testid="member-login-modal">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={close}
              className="absolute inset-0 bg-black/85 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md border border-[#c9a227]/30 bg-[#0a0f0a] px-6 py-12 text-center shadow-[0_0_80px_rgba(201,162,39,0.12)] md:px-12"
            >
              <button
                data-testid="member-login-close"
                onClick={close}
                aria-label="Close"
                className="absolute right-4 top-4 text-[#fdfbf7]/40 transition-colors duration-300 hover:text-[#e8c95a]"
              >
                <X size={20} strokeWidth={1.5} />
              </button>

              {view === "login" && (
                <>
                  <KeyRound className="mx-auto text-[#e8c95a]" size={24} strokeWidth={1.2} />
                  <h2 className="font-serif-engraved mt-4 text-2xl font-light uppercase tracking-wide sm:text-3xl">
                    Members <span className="gold-shimmer">Sign In</span>
                  </h2>
                  <p className="mx-auto mt-3 max-w-xs text-sm font-light italic leading-relaxed text-[#fdfbf7]/55">
                    Present your key, and the salon opens.
                  </p>
                  <form data-testid="member-login-form" onSubmit={submit} className="mt-8 flex flex-col gap-4">
                    <input
                      data-testid="member-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your correspondence address"
                      className={inputClass}
                    />
                    <input
                      data-testid="member-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your key"
                      className={inputClass}
                    />
                    {error && (
                      <p data-testid="member-login-error" className="text-sm font-light italic text-[#e08a8a]">
                        {error}
                      </p>
                    )}
                    <button data-testid="member-login-submit" type="submit" disabled={loading} className={submitClass}>
                      <span className="absolute inset-0 -translate-x-full bg-[#c9a227] transition-transform duration-500 ease-out group-hover:translate-x-0" />
                      <span className="relative">{loading ? "Turning the key…" : "Unlock the Salon"}</span>
                    </button>
                  </form>
                  <button
                    data-testid="member-lost-key-link"
                    onClick={() => switchView("recover")}
                    className="mt-6 text-[11px] uppercase tracking-[0.25em] text-[#fdfbf7]/35 transition-colors duration-300 hover:text-[#e8c95a]"
                  >
                    Lost your key?
                  </button>
                </>
              )}

              {view === "recover" && (
                <>
                  <KeyRound className="mx-auto text-[#e8c95a]" size={24} strokeWidth={1.2} />
                  <h2 className="font-serif-engraved mt-4 text-2xl font-light uppercase tracking-wide sm:text-3xl">
                    A <span className="gold-shimmer">Lost Key</span>
                  </h2>
                  <p className="mx-auto mt-3 max-w-xs text-sm font-light italic leading-relaxed text-[#fdfbf7]/55">
                    Give the archive your correspondence address, and a fresh key
                    shall be cut and sent by sealed letter.
                  </p>
                  <form data-testid="member-recovery-form" onSubmit={submitRecovery} className="mt-8 flex flex-col gap-4">
                    <input
                      data-testid="member-recovery-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your correspondence address"
                      className={inputClass}
                    />
                    {error && (
                      <p data-testid="member-recovery-error" className="text-sm font-light italic text-[#e08a8a]">
                        {error}
                      </p>
                    )}
                    <button data-testid="member-recovery-submit" type="submit" disabled={loading} className={submitClass}>
                      <span className="absolute inset-0 -translate-x-full bg-[#c9a227] transition-transform duration-500 ease-out group-hover:translate-x-0" />
                      <span className="relative">{loading ? "Cutting the key…" : "Send a Fresh Key"}</span>
                    </button>
                  </form>
                  <button
                    data-testid="member-recovery-back"
                    onClick={() => switchView("login")}
                    className="mt-6 text-[11px] uppercase tracking-[0.25em] text-[#fdfbf7]/35 transition-colors duration-300 hover:text-[#e8c95a]"
                  >
                    Back to sign in
                  </button>
                </>
              )}

              {view === "sent" && (
                <div data-testid="member-recovery-success">
                  <MailOpen className="mx-auto text-[#e8c95a]" size={24} strokeWidth={1.2} />
                  <h2 className="font-serif-engraved mt-4 text-2xl font-light uppercase tracking-wide sm:text-3xl">
                    The Letter <span className="gold-shimmer">Flies</span>
                  </h2>
                  <p className="mx-auto mt-4 max-w-xs text-sm font-light italic leading-relaxed text-[#fdfbf7]/55">
                    If your name is inscribed in the archive, a fresh key is
                    travelling to your inbox beneath a wax seal. Present it at
                    the door when it arrives.
                  </p>
                  <button
                    data-testid="member-recovery-return"
                    onClick={() => switchView("login")}
                    className={`${submitClass} mt-8 px-8`}
                  >
                    <span className="absolute inset-0 -translate-x-full bg-[#c9a227] transition-transform duration-500 ease-out group-hover:translate-x-0" />
                    <span className="relative">Return to Sign In</span>
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MemberLogin;
